import { useCallback } from 'react'
import type { EnhancedProductFamilyId, EnhancedProductGroupId, EnhancedProductVariantId } from '#api/enhancements/enhancedTmsApiTypes'
import { useRegisterCustomReportData } from '#features/ErrorHandling/useRegisterCustomReportData'
import type { CustomReportData, CustomReportDataProvider } from '#features/Feedback/buildReport'
import type { Formatter } from '#features/Feedback/formatters'
import { log } from '#utils/logger'

export const injectReportDataFields = ({
  families,
  variants,
  groups,
}: {
  families?: EnhancedProductFamilyId | EnhancedProductFamilyId[]
  variants?: EnhancedProductVariantId | EnhancedProductVariantId[]
  groups?: EnhancedProductGroupId | EnhancedProductGroupId[]
}) => {
  return {
    'data-tms-family': Array.isArray(families) ? families.join(' ') : families,
    'data-tms-variant': Array.isArray(variants) ? variants.join(' ') : variants,
    'data-tms-group': Array.isArray(groups) ? groups.join(' ') : groups,
  } as const
}

export const injectProductGroupFields = (groups: EnhancedProductGroupId | EnhancedProductGroupId[] | undefined) => {
  if (!groups) {
    return undefined
  }

  return {
    'data-tms-group': Array.isArray(groups) ? groups.join(' ') : groups,
  } as const
}

export const injectProductFamilyFields = (families: EnhancedProductFamilyId | EnhancedProductFamilyId[] | undefined) => {
  if (!families) {
    return undefined
  }

  return {
    'data-tms-family': Array.isArray(families) ? families.join(' ') : families,
  } as const
}

export const injectProductVariantFields = (variants: EnhancedProductVariantId | EnhancedProductVariantId[] | undefined) => {
  if (!variants) {
    return undefined
  }

  return {
    'data-tms-variant': Array.isArray(variants) ? variants.join(' ') : variants,
  } as const
}

const collectReportDataFields = (node: Element) => {
  const families = node.getAttribute('data-tms-family')
  const variants = node.getAttribute('data-tms-variant')
  const groups = node.getAttribute('data-tms-group')

  return {
    families: families ? families.split(' ') : undefined,
    variants: variants ? variants.split(' ') : undefined,
    groups: groups ? groups.split(' ') : undefined,
  }
}

export const collectReportDataFieldsFromHierarchy = (node: HTMLElement) => {
  const data = collectReportDataFields(node)

  let parent = node.parentElement
  let hasAllFields = data.families && data.variants && data.groups
  while (parent && !hasAllFields) {
    const parentData = collectReportDataFields(parent)
    if (parentData.groups && !data.groups) {
      data.groups = parentData.groups
    }
    if (parentData.families && !data.families) {
      data.families = parentData.families
    }
    if (parentData.variants && !data.variants) {
      data.variants = parentData.variants
    }
    hasAllFields = data.families && data.variants && data.groups
    parent = parent.parentElement
  }

  // find fields in children, when not found from parent
  if (!hasAllFields) {
    const childNodes = node.querySelectorAll('[data-tms-group], [data-tms-family], [data-tms-variant]')
    for (const node of childNodes) {
      const { groups, families, variants } = collectReportDataFields(node)
      data.groups = groups ? [...new Set([...(data.groups ?? []), ...groups])] : data.groups
      data.families = families ? [...new Set([...(data.families ?? []), ...families])] : data.families
      data.variants = variants ? [...new Set([...(data.variants ?? []), ...variants])] : data.variants
    }
  }

  log.log('Found context data fields for node', node, data)

  return data
}

export function formatContextDataFields(node: HTMLElement, formatter: Formatter) {
  const data = collectReportDataFieldsFromHierarchy(node)

  return (
    [
      data.groups ? `- ${formatter.bold('Product Groups')}: ${formatter.inlineCode(data.groups.join(', '))}` : undefined,
      data.families ? `- ${formatter.bold('Product Families')}: ${formatter.inlineCode(data.families.join(', '))}` : undefined,
      data.variants ? `- ${formatter.bold('Product Variants')}: ${formatter.inlineCode(data.variants.join(', '))}` : undefined,
    ]
      .filter(Boolean)
      .join('\n') || formatter.bold('No context data fields found')
  )
}

export interface ReportDataFieldProps {
  'data-tms-family'?: string
  'data-tms-variant'?: string
  'data-tms-group'?: string
}

export const useInjectedReportDataCollector = () => {
  const reporter = useCallback<CustomReportDataProvider>(function InjectedReportDataCollector(formatter, reportData): CustomReportData | null {
    const target = reportData.htmlElement
    if (target instanceof HTMLElement) {
      return {
        title: 'Context Data',
        data: formatContextDataFields(target, formatter),
      }
    }
    return null
  }, [])

  useRegisterCustomReportData(reporter)
}
